/** *
 * based on vuelidate documentation
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#i18n-support
 *
 */

import * as validators from '@vuelidate/validators';
import { createI18nMessage, helpers, MessageProps } from '@vuelidate/validators';
import { i18n } from '@/lang';
import Backend, { adminLevels, AppGroups } from './backend';

// change the path for fetching validator messages
const messagePath = ({ $validator }: MessageProps) => `adm.validations.${$validator}`;

// function responsible for doing the actual translation
const withI18nMessage = createI18nMessage({ t: i18n.global.t.bind(i18n), messagePath });

// wrap each validator.
export const required = withI18nMessage(validators.required);
export const minLength = withI18nMessage(validators.minLength, { withArguments: true });
export const maxLength = withI18nMessage(validators.maxLength, { withArguments: true });
export const validEmail = withI18nMessage(validators.email);
export const validAdminLevel = withI18nMessage((value: string) => adminLevels.filter((i) => i === value).length > 0);

const checkEmailInUse = (async (email: string) => {
  // Logger.info("validator:checkEmailInUse", "start", email);

  // exit - to avoid making a call with an invalid email
  // true - because we don't want to show the error message in this case
  if (!email || email.length === 0) return true;

  if (!validators.email.$validator(email, null, null)) {
    // exit - to avoid making a call with an invalid email
    // true - because we don't want to show the error message in this case
    return true;
  }

  return Backend.validateToken().then(async (api) => !await api.userExists(email));
});
export const emailNotInUse = withI18nMessage(helpers.withAsync(checkEmailInUse), { withArguments: true });

/** contains at least 1 lowercase letter */
export const hasLowercase = withI18nMessage(validators.helpers.regex(/^(?=.*[a-z]).*$/u));

/** contains at least 1 uppercase letter */
export const hasUppercase = withI18nMessage(validators.helpers.regex(/^(?=.*[A-Z]).*$/u));

/** contains at least 1 digit */
export const hasDigit = withI18nMessage(validators.helpers.regex(/^(?=.*[0-9]).*$/u));

/** contains at least 1 special character */
export const hasSpecial = (val: string) => validators.helpers.withParams(
  { chars: val },
  withI18nMessage(validators.helpers.regex(/^(?=.*[!&*\-+=();."'?$%~#<>:,|§\\/@^_[\]{}]).*$/u)),
);

/** does not contain invalid characters */
export const invalidCharacters = withI18nMessage(
  validators.helpers.regex(/^[\w\p{L}!&*\-+=();."'?$%~#<>:,|§\\/@^_[\]{}]*$/u),
);

/**
 * @author https://www.geradorcpf.com/javascript-validar-cpf.htm
 */
const validateCPF = (input: string) => {
  const cpf = input.replace(/[^\d]+/g, '');
  if (cpf === '') return false;
  // Elimina CPFs invalidos conhecidos
  if (cpf.length !== 11
    || cpf === '00000000000'
    || cpf === '11111111111'
    || cpf === '22222222222'
    || cpf === '33333333333'
    || cpf === '44444444444'
    || cpf === '55555555555'
    || cpf === '66666666666'
    || cpf === '77777777777'
    || cpf === '88888888888'
    || cpf === '99999999999') {
    return false;
  }
  // Valida 1o digito
  let add = 0;
  for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i), 10) * (10 - i);
  let rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9), 10)) return false;
  // Valida 2o digito
  add = 0;
  for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i), 10) * (11 - i);
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(10), 10)) return false;
  return true;
};

// const CPF = (val: string) => /\d{11}/.test(val);

/** valid PIN: Brazilian CPF */
export const validPIN = withI18nMessage((val: string) => !helpers.req(val) || validateCPF(val));

const checkCPFInUse = (async (cpf: string) => {
  // Logger.info("validator:checkEmailInUse", "start", email);

  // exit - to avoid making a call with an invalid email
  // true - because we don't want to show the error message in this case
  if (!cpf || cpf.length === 0) return true;

  if (!validateCPF(cpf)) {
    // exit - to avoid making a call with an invalid email
    // true - because we don't want to show the error message in this case
    return true;
  }

  return Backend.validateToken().then(async (api) => !await api.userExists(undefined, cpf));
});

export const cpfNotInUse = withI18nMessage(helpers.withAsync(checkCPFInUse), { withArguments: true });

/**
 * validate the user is at least 18 years old
 * @param birthdate user's birthdate
 * @param age age of adulthood
 */
const isAdult = (birthdate: string, age: number) => {
  const eighteenthBirthday = new Date(birthdate);
  eighteenthBirthday.setFullYear(eighteenthBirthday.getFullYear() + age);

  return new Date() >= eighteenthBirthday;
};
export const mustBeAdult = withI18nMessage((val: string) => !helpers.req(val) || isAdult(val, 18));

const regexAppGroups = new RegExp('^' + AppGroups.map(i => i.id).join('|') + '$', 'u');
export const validAppGroup = withI18nMessage(
  //validators.helpers.regex(/^group-ssc-api|group-inventory-api$/u),
  validators.helpers.regex(regexAppGroups),
);
